/**
 * ソロ・ドリル UI（選択モーダル＋セッション進捗ゲージ）。自前・GPL非依存。
 * 設計: knowledge/architecture/solo-drill-daily-challenge.md / solo-drill-catalog.md
 *
 * 責務分離:
 *   - 本モジュールは「ドリルの選択 UI」と「セッション中の進捗表示」を組み立てる DOM ファクトリ。
 *   - 盤面への配置・合否判定・モード切替は main.ts が担う（onStart コールバック経由）。
 *   - エントリ（🎯 トップバーボタン）は controls.ts が持ち、押下で open() を呼ぶ（deps.onOpenDrill）。
 *
 * HUD 制約（mobile-hud-ux-guidelines.md）: モーダルは pool-modal を流用（中央オーバーレイ）。
 * セッションゲージは上中央へ固定だが、プラクティス配置パレットと時間的に競合しない
 * （ドリルは配置モードに入らず固定配置のため）。文言は textContent で組み立てる（innerHTML 禁止）。
 */

import {
  DRILL_RANKS,
  drillsByRank,
  drillMaxShots,
  getRankDef,
  isRankUnlocked,
  nextDrillAcrossRanks,
  suggestActiveRank,
  type DrillLayout,
  type DrillRank,
} from '../sim/drill-layouts.js';
import type { DrillProgress } from '../state/drill-progress.js';

export interface DrillPanelDeps {
  /** ドリル選択時（一覧の「開始」/クリア後の「次へ」「もう一度」）。main が盤面へ配置しセッションを開始する。 */
  readonly onStart: (layout: DrillLayout) => void;
  /** 現在のローカル進捗（クリア済み・ノーミス）を返す。 */
  readonly getProgress: () => DrillProgress;
  /** ドリルを終了して入る前のプレイモードへ戻す（クリア後の「終了」/ゲージから抜ける）。 */
  readonly onExit: () => void;
}

export interface DrillGaugeState {
  readonly remaining: number;
  readonly total: number;
  readonly shotsUsed: number;
  readonly maxShots: number;
  readonly misses: number;
}

export interface DrillPanelApi {
  open: () => void;
  close: () => void;
  /** セッション開始（ゲージ表示）。 */
  startSession: (layout: DrillLayout) => void;
  /** 1 ショット後のゲージ更新（残球・ショット消費・ノーミス継続を表示）。 */
  updateGauge: (state: DrillGaugeState) => void;
  /** 取り切り達成表示（noMiss=true で称号）。 */
  showCleared: (layout: DrillLayout, noMiss: boolean) => void;
  /** ショット上限超過 → 最初からやり直し表示。 */
  showFailed: (layout: DrillLayout) => void;
  /** セッション終了（ゲージ非表示）。 */
  endSession: () => void;
}

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  parent: HTMLElement,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  parent.appendChild(node);
  return node;
};

export const createDrillPanel = (deps: DrillPanelDeps): DrillPanelApi => {
  // --- 選択モーダル ---
  const modal = el('div', 'pool-modal', document.body);
  modal.hidden = true;
  const box = el('div', 'pool-modal-box pool-drill-box', modal);
  const title = el('h3', 'pool-modal-title', box);
  title.textContent = '🎯 ドリル';
  const desc = el('div', 'pool-modal-desc', box);
  desc.textContent = '配置をノーミスで取り切る練習。ランクは前のランクを規定数クリアで解放されます。';

  // ランク切替タブ
  const tabs = el('div', 'pool-drill-tabs', box);
  const list = el('div', 'pool-drill-list', box);
  const closeBtn = el('button', 'pool-btn', box);
  closeBtn.textContent = '閉じる';
  closeBtn.addEventListener('click', () => {
    modal.hidden = true;
  });

  let activeRank: DrillRank = 'C';

  const renderList = (): void => {
    const progress = deps.getProgress();
    const cleared = new Set(progress.clearedIds);
    const noMiss = new Set(progress.noMissIds);
    const unlocked = isRankUnlocked(activeRank, progress.clearedIds);

    while (list.firstChild) list.removeChild(list.firstChild);

    if (!unlocked) {
      const def = getRankDef(activeRank);
      const prev = DRILL_RANKS.find((d) => d.order === def.order - 1);
      const lock = el('div', 'pool-drill-locked', list);
      lock.textContent = prev
        ? `🔒 ${prev.label}を ${def.unlockClearsNeeded} 個クリアすると解放されます。`
        : '🔒 まだ解放されていません。';
      return;
    }

    for (const drill of drillsByRank(activeRank)) {
      const row = el('div', 'pool-drill-row', list);
      const isCleared = cleared.has(drill.id);
      const isNoMiss = noMiss.has(drill.id);
      if (isCleared) row.classList.add('is-cleared');
      const nameEl = el('div', 'pool-drill-name', row);
      const mark = isNoMiss ? '🥇 ' : isCleared ? '✅ ' : '';
      nameEl.textContent = `${mark}${drill.name}（${drill.balls.length}球）`;
      const startBtn = el('button', 'pool-btn', row);
      startBtn.textContent = '開始';
      startBtn.addEventListener('click', () => {
        modal.hidden = true;
        deps.onStart(drill);
      });
    }
  };

  const renderTabs = (): void => {
    const progress = deps.getProgress();
    while (tabs.firstChild) tabs.removeChild(tabs.firstChild);
    for (const def of [...DRILL_RANKS].sort((a, b) => a.order - b.order)) {
      const tab = el('button', 'pool-btn pool-drill-tab', tabs);
      const unlocked = isRankUnlocked(def.rank, progress.clearedIds);
      tab.textContent = unlocked ? def.label : `🔒${def.label}`;
      if (def.rank === activeRank) tab.classList.add('is-active');
      tab.addEventListener('click', () => {
        activeRank = def.rank;
        renderTabs();
        renderList();
      });
    }
  };

  const open = (): void => {
    modal.hidden = false;
    // 開く時のアクティブランクを自動調整: 現在ランクに未クリアが残ればそのまま、
    // 全クリア済みなら解放済みの次ランクへ移す（ランク跨ぎ進行）。
    const progress = deps.getProgress();
    activeRank = suggestActiveRank(activeRank, progress.clearedIds);
    renderTabs();
    renderList();
  };
  const close = (): void => {
    modal.hidden = true;
  };

  // --- セッション進捗ゲージ（上中央・固定） ---
  const gauge = el('div', 'pool-drill-gauge', document.body);
  gauge.hidden = true;
  const gaugeTitle = el('div', 'pool-drill-gauge-title', gauge);
  const gaugeStatus = el('div', 'pool-drill-gauge-status', gauge);
  // クリア/失敗時の遷移ボタン（次へ/もう一度/終了）。プレイ中は空。
  const gaugeActions = el('div', 'pool-drill-gauge-actions', gauge);
  gaugeActions.hidden = true;

  const clearActions = (): void => {
    while (gaugeActions.firstChild) gaugeActions.removeChild(gaugeActions.firstChild);
    gaugeActions.hidden = true;
  };
  const addAction = (label: string, onClick: () => void): void => {
    const btn = el('button', 'pool-btn', gaugeActions);
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    gaugeActions.hidden = false;
  };

  const startSession = (layout: DrillLayout): void => {
    gauge.hidden = false;
    gauge.classList.remove('is-miss', 'is-cleared');
    clearActions();
    const n = layout.balls.length;
    const max = drillMaxShots(layout);
    gaugeTitle.textContent = `🎯 ${getRankDef(layout.rank).label} ${layout.name}`;
    gaugeStatus.textContent = `残り ${n}球　ショット 0/${max}`;
  };

  const updateGauge = (state: DrillGaugeState): void => {
    gauge.classList.remove('is-miss', 'is-cleared');
    // 上限が迫ったら警告色（残りショットが残球数以下）。
    const shotsLeft = state.maxShots - state.shotsUsed;
    if (shotsLeft <= state.remaining) gauge.classList.add('is-miss');
    // 主表示=残球・ショット消費。ノーミスはボーナスとして控えめに添える。
    const bonus = state.misses === 0 ? '　★ノーミス継続' : '';
    gaugeStatus.textContent = `残り ${state.remaining}球　ショット ${state.shotsUsed}/${state.maxShots}${bonus}`;
  };

  const showCleared = (layout: DrillLayout, noMiss: boolean): void => {
    gauge.hidden = false;
    gauge.classList.remove('is-miss');
    gauge.classList.add('is-cleared');
    gaugeTitle.textContent = noMiss ? '🥇 ノーミス取り切り達成！' : '✅ 達成！';
    gaugeStatus.textContent = noMiss
      ? `${getRankDef(layout.rank).label} ${layout.name}　称号獲得`
      : `${getRankDef(layout.rank).label} ${layout.name}　クリア`;
    // 次ドリル導線: 同ランクの次へ、なければ次ランク（解放済み）の先頭へ進む。
    // ランクを跨ぐ場合はボタン文言を「▶ 次のランク（X級）へ」に切り替えて UX を明示する。
    clearActions();
    const progress = deps.getProgress();
    const next = nextDrillAcrossRanks(layout, progress.clearedIds);
    if (next) {
      const crossRank = next.rank !== layout.rank;
      const label = crossRank
        ? `▶ 次のランク（${getRankDef(next.rank).label}）へ`
        : '▶ 次のドリル';
      addAction(label, () => deps.onStart(next));
    }
    addAction('↻ もう一度', () => deps.onStart(layout));
    addAction('一覧', () => {
      // 一覧ボタンから開く場合、次に取り組むべきランクへフォーカスを当てる
      //（クリア後にすぐ「一覧」を押した時、現在ランクが全クリア済みなら次ランクへ）。
      activeRank = suggestActiveRank(next ? next.rank : layout.rank, progress.clearedIds);
      open();
    });
    addAction('✕ 終了', () => deps.onExit());
  };

  const showFailed = (layout: DrillLayout): void => {
    gauge.hidden = false;
    gauge.classList.remove('is-cleared');
    gauge.classList.add('is-miss');
    clearActions();
    gaugeTitle.textContent = '🔁 ショット上限超過';
    gaugeStatus.textContent = `${getRankDef(layout.rank).label} ${layout.name}　最初からやり直し`;
  };

  const endSession = (): void => {
    gauge.hidden = true;
    clearActions();
  };

  return { open, close, startSession, updateGauge, showCleared, showFailed, endSession };
};
